import type { PrivateEvent } from '../../api/privateEvents';
import { WorkshopListOpenButton } from '../dashboards/primitives/WorkshopListOpenButton';

import IncompleteMarker from './IncompleteMarker';
import { formatDealValue, formatLocalDate, staffingLabel } from './privateEventDisplay';
import PrivateEventLocationCell from './PrivateEventLocationCell';

const CARD_RADIUS = 12;

/**
 * One card of `PrivateEventsCardList`. Its own file, per house rule.
 *
 * The click target is a real, transparent button laid over the card (same
 * pattern as `WorkshopListItem`), not an onClick on the card itself — that is
 * what makes the card openable from the keyboard and gives it an accessible
 * name, rather than being a mouse-only affordance.
 */
export default function PrivateEventCard({
  event,
  onOpen,
}: {
  event: PrivateEvent;
  onOpen?: (event: PrivateEvent) => void;
}) {
  const staffing = staffingLabel(event);
  return (
    <article
      className="nb-card nb-card-tight"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        position: 'relative',
        isolation: 'isolate',
      }}
    >
      {onOpen && (
        <WorkshopListOpenButton
          label={`Open ${event.clientName}`}
          radius={CARD_RADIUS}
          onActivate={() => onOpen(event)}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nb-ink)' }}>
          {event.clientName}
        </span>
        <IncompleteMarker event={event} />
      </div>

      <div
        className="nb-tabular"
        style={{ fontSize: 12, fontWeight: 600, color: 'var(--nb-muted)' }}
      >
        {formatLocalDate(event.localStartsAt, event.startsAt)}
      </div>

      <div style={{ fontSize: 12, color: 'var(--nb-muted)' }}>
        <PrivateEventLocationCell event={event} short />
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: 12,
          fontSize: 12,
          paddingTop: 2,
        }}
      >
        <span className="nb-tabular" style={{ color: 'var(--nb-ink)', fontWeight: 700 }}>
          {event.headcount}
          <span style={{ marginLeft: 4, fontWeight: 400, color: 'var(--nb-subtle)' }}>guests</span>
        </span>
        <span style={{ fontWeight: 600, color: staffing.color }}>{staffing.text}</span>
        <span className="nb-tabular" style={{ color: 'var(--nb-muted)' }}>
          {formatDealValue(event.dealValueCents)}
          {event.dealValueCents !== null && (
            <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--nb-faint)' }}>est.</span>
          )}
        </span>
      </div>
    </article>
  );
}
